import { LOCALE_CODES, type Locale } from './locales.js';

/**
 * Нормализация «сырого» кода языка (IETF-тег из Telegram `language_code`,
 * `navigator.language` и т.п.) к одному из поддерживаемых (SUPPORTED_LOCALES).
 *
 * Единый источник правды и для клиента (detectLocale в mini app), и для
 * сервера (бот, API) — чтобы маппинг не разъезжался между двумя копиями.
 */

const SUPPORTED = new Set<string>(LOCALE_CODES);

/**
 * Спец-маппинг кодов, которые не совпадают с нашими «витринными» 1:1:
 * у нас pt-br/zh-hans как базовые варианты — приводим к ним близкие коды.
 */
const SPECIAL: Record<string, Locale> = {
  pt: 'pt-br',
  zh: 'zh-hans',
  'zh-cn': 'zh-hans',
  'zh-sg': 'zh-hans',
  'zh-hk': 'zh-hant',
  'zh-tw': 'zh-hant',
  'zh-mo': 'zh-hant',
};

/**
 * Привести сырой код к поддерживаемому `Locale` или вернуть null, если язык
 * не распознан. Порядок: точное совпадение → спец-маппинг полного тега →
 * базовый язык (es-mx → es) → спец-маппинг базового.
 */
export function normalizeLocale(raw: string | undefined | null): Locale | null {
  if (!raw) return null;
  const lc = raw.toLowerCase();
  if (SUPPORTED.has(lc)) return lc as Locale;
  if (SPECIAL[lc]) return SPECIAL[lc];
  const base = lc.split('-')[0] ?? lc;
  if (SUPPORTED.has(base)) return base as Locale;
  if (SPECIAL[base]) return SPECIAL[base];
  return null;
}

/**
 * Серверное определение языка пользователя по данным из БД.
 * Приоритет: явный выбор пользователя (`User.locale`, переключатель в mini app)
 * → код языка Telegram (`User.languageCode`) → дефолт `en`.
 *
 * Используется ботом и API, где нет доступа к browser/SDK launch params.
 */
export function resolveLocale(input: {
  locale?: string | null;
  languageCode?: string | null;
}): Locale {
  return normalizeLocale(input.locale) ?? normalizeLocale(input.languageCode) ?? 'en';
}
